/**
 * The platform registry.
 *
 * Adding a platform means adding one entry here. Everything else, the URL
 * parsing, the icon, the ordering, the label on the tile, the resolver that
 * fetches a profile picture, reads off this list rather than off a switch
 * statement somewhere in a component.
 */

export type PlatformKey =
  | "instagram"
  | "onlyfans"
  | "reddit"
  | "twitter"
  | "tiktok"
  | "facebook"
  | "threads"
  | "youtube"
  | "snapchat"
  | "telegram"
  | "fansly"
  | "linktree"
  | "pornhub"
  | "chaturbate"
  | "xvideos"
  | "xhamster"
  | "stripchat"
  | "manyvids"
  | "redgifs"
  | "bongacams"
  | "cam4"
  | "livejasmin"
  | "myfreecams"
  | "clips4sale"
  | "fetlife"
  | "throne"
  | "other";

export type Platform = {
  key: PlatformKey;
  label: string;
  /** Brand colour, muted to sit inside the theme rather than shout over it. */
  colour: string;
  /** Hosts that identify this platform in a pasted URL. */
  hosts: string[];
  /** Pulls the handle out of a URL path. */
  handleFrom: (url: URL) => string | null;
  /** Rebuilds a canonical URL from a handle, so two people pasting differently land on one row. */
  urlFor: (handle: string) => string;
  /**
   * How a profile is read, if at all.
   *
   *  session  needs one of the tracker's Instagram cookie jars
   *  api      a JSON endpoint that needs no credentials
   *  public   Open Graph tags on the profile page
   *  none     behind a sign-in wall, never attempted
   *
   * Kept as a mode rather than a boolean so a tile can say WHY it has no
   * picture. "OnlyFans profiles are behind a sign-in wall" and "could not read
   * that just now" are different facts, and showing the second for the first is
   * how a panel trains people to ignore it.
   */
  resolve: ResolveMode;
  /** Shown on the tile when a read is impossible rather than merely unlucky. */
  blocked?: string;
};

export type ResolveMode = "session" | "api" | "public" | "none";

const firstSegment = (url: URL): string | null => {
  const segment = url.pathname.split("/").filter(Boolean)[0];
  return segment ? segment.replace(/^@/, "") : null;
};

const afterSegment =
  (...prefixes: string[]) =>
  (url: URL): string | null => {
    const parts = url.pathname.split("/").filter(Boolean);
    const index = parts.findIndex((part) => prefixes.includes(part.toLowerCase()));
    const value = index >= 0 ? parts[index + 1] : parts[0];
    return value ? value.replace(/^@/, "") : null;
  };

export const PLATFORMS: Platform[] = [
  {
    key: "instagram",
    label: "Instagram",
    colour: "#d47b79",
    hosts: ["instagram.com", "instagr.am", "ig.me"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://www.instagram.com/${handle}/`,
    resolve: "session",
  },
  {
    key: "onlyfans",
    label: "OnlyFans",
    colour: "#93a7c2",
    hosts: ["onlyfans.com", "of.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://onlyfans.com/${handle}`,
    resolve: "none",
    blocked: "OnlyFans profiles sit behind Cloudflare and a sign-in wall",
  },
  {
    key: "reddit",
    label: "Reddit",
    colour: "#c98b6b",
    hosts: ["reddit.com", "old.reddit.com", "np.reddit.com"],
    handleFrom: afterSegment("u", "user"),
    urlFor: (handle) => `https://www.reddit.com/user/${handle}/`,
    // Reddit's about.json needs no credentials but its bot protection answers
    // 403 to whole networks at a time, so a read here is genuinely best effort.
    // The username itself comes from the Drive sheet and never depends on this.
    resolve: "api",
  },
  {
    key: "twitter",
    label: "X",
    colour: "#9aa4ad",
    hosts: ["twitter.com", "x.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://x.com/${handle}`,
    resolve: "public",
  },
  {
    key: "tiktok",
    label: "TikTok",
    colour: "#7fbfc4",
    hosts: ["tiktok.com", "vm.tiktok.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://www.tiktok.com/@${handle}`,
    resolve: "public",
  },
  {
    key: "facebook",
    label: "Facebook",
    colour: "#8296bd",
    hosts: ["facebook.com", "fb.com", "m.facebook.com"],
    handleFrom: afterSegment("profile.php", "people"),
    urlFor: (handle) => `https://www.facebook.com/${handle}`,
    resolve: "public",
  },
  {
    key: "threads",
    label: "Threads",
    colour: "#a3a8ad",
    hosts: ["threads.net", "threads.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://www.threads.net/@${handle}`,
    resolve: "public",
  },
  {
    key: "youtube",
    label: "YouTube",
    colour: "#cf7b7b",
    hosts: ["youtube.com", "youtu.be"],
    handleFrom: afterSegment("c", "channel", "user"),
    urlFor: (handle) => `https://www.youtube.com/@${handle}`,
    resolve: "public",
  },
  {
    key: "snapchat",
    label: "Snapchat",
    colour: "#c7b96b",
    hosts: ["snapchat.com"],
    handleFrom: afterSegment("add", "t"),
    urlFor: (handle) => `https://www.snapchat.com/add/${handle}`,
    resolve: "none",
    blocked: "Snapchat serves no public profile to read",
  },
  {
    key: "telegram",
    label: "Telegram",
    colour: "#84a8c4",
    hosts: ["t.me", "telegram.me"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://t.me/${handle}`,
    resolve: "public",
  },
  {
    key: "fansly",
    label: "Fansly",
    colour: "#7f9ec4",
    hosts: ["fansly.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://fansly.com/${handle}`,
    resolve: "none",
    blocked: "Fansly profiles sit behind a sign-in wall",
  },
  /*
   * The tube and cam sites. No icon set publishes these, so they are drawn as a
   * monogram in the brand's own colour rather than as a logo drawn from memory.
   * Everything else about them works the same way: paste a link and the handle,
   * the canonical URL and the platform fall out of it.
   */
  {
    key: "pornhub",
    label: "Pornhub",
    colour: "#FF9000",
    hosts: ["pornhub.com", "pornhubpremium.com"],
    handleFrom: afterSegment("model", "pornstar", "users", "channels"),
    urlFor: (handle) => `https://www.pornhub.com/model/${handle}`,
    resolve: "public",
  },
  {
    key: "chaturbate",
    label: "Chaturbate",
    colour: "#F47321",
    hosts: ["chaturbate.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://chaturbate.com/${handle}/`,
    resolve: "public",
  },
  {
    key: "xvideos",
    label: "XVideos",
    colour: "#C9211E",
    hosts: ["xvideos.com", "xvideos.red"],
    handleFrom: afterSegment("profiles", "models", "channels"),
    urlFor: (handle) => `https://www.xvideos.com/profiles/${handle}`,
    resolve: "public",
  },
  {
    key: "xhamster",
    label: "xHamster",
    colour: "#E36C0A",
    hosts: ["xhamster.com", "xhamster.desi"],
    handleFrom: afterSegment("users", "creators"),
    urlFor: (handle) => `https://xhamster.com/users/${handle}`,
    resolve: "public",
  },
  {
    key: "stripchat",
    label: "Stripchat",
    colour: "#E51F5E",
    hosts: ["stripchat.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://stripchat.com/${handle}`,
    resolve: "public",
  },
  {
    key: "manyvids",
    label: "ManyVids",
    colour: "#43B0E4",
    hosts: ["manyvids.com"],
    handleFrom: afterSegment("profile"),
    urlFor: (handle) => `https://www.manyvids.com/Profile/${handle}`,
    resolve: "public",
  },
  {
    key: "redgifs",
    label: "RedGIFs",
    colour: "#F44336",
    hosts: ["redgifs.com"],
    handleFrom: afterSegment("users"),
    urlFor: (handle) => `https://www.redgifs.com/users/${handle}`,
    resolve: "public",
  },
  {
    key: "bongacams",
    label: "BongaCams",
    colour: "#D8232A",
    hosts: ["bongacams.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://bongacams.com/${handle}`,
    resolve: "public",
  },
  {
    key: "cam4",
    label: "Cam4",
    colour: "#F7941E",
    hosts: ["cam4.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://www.cam4.com/${handle}`,
    resolve: "public",
  },
  {
    key: "livejasmin",
    label: "LiveJasmin",
    colour: "#C8102E",
    hosts: ["livejasmin.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://www.livejasmin.com/en/chat/${handle}`,
    resolve: "public",
  },
  {
    key: "myfreecams",
    label: "MyFreeCams",
    colour: "#0F70B7",
    hosts: ["myfreecams.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://profiles.myfreecams.com/${handle}`,
    resolve: "public",
  },
  {
    key: "clips4sale",
    label: "Clips4Sale",
    colour: "#E4002B",
    hosts: ["clips4sale.com"],
    handleFrom: afterSegment("studio"),
    urlFor: (handle) => `https://www.clips4sale.com/studio/${handle}`,
    resolve: "public",
  },
  {
    key: "fetlife",
    label: "FetLife",
    colour: "#B3282D",
    hosts: ["fetlife.com"],
    handleFrom: afterSegment("users"),
    urlFor: (handle) => `https://fetlife.com/users/${handle}`,
    resolve: "public",
  },
  {
    key: "throne",
    label: "Throne",
    colour: "#7C3AED",
    hosts: ["throne.com", "throne.me"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://throne.com/${handle}`,
    resolve: "public",
  },
  {
    key: "linktree",
    label: "Links",
    colour: "#7f9e8c",
    hosts: ["linktr.ee", "onlylinks.com", "beacons.ai", "allmylinks.com"],
    handleFrom: firstSegment,
    urlFor: (handle) => `https://linktr.ee/${handle}`,
    resolve: "public",
  },
  {
    key: "other",
    label: "Other",
    colour: "#8f979d",
    hosts: [],
    handleFrom: firstSegment,
    urlFor: (handle) => handle,
    resolve: "public",
  },
];

export const PLATFORM_ORDER: PlatformKey[] = PLATFORMS.map((platform) => platform.key);

export function platform(key: string): Platform {
  return PLATFORMS.find((entry) => entry.key === key) ?? PLATFORMS[PLATFORMS.length - 1];
}

export function platformLabel(key: string): string {
  return platform(key).label;
}

export function platformColour(key: string): string {
  return platform(key).colour;
}

/** True when a picture is worth attempting at all. */
export function isResolvable(key: string): boolean {
  return platform(key).resolve !== "none";
}

export type ParsedAccount = { platform: PlatformKey; url: string; handle: string | null };

/**
 * Turn whatever someone pasted into a platform, a canonical URL and a handle.
 *
 * Deliberately forgiving about the input, because the whole point of the add box
 * is that you paste a link and get on with your day: a bare handle, a mobile
 * URL, a share link with tracking parameters and a URL missing its scheme all
 * land on the same row.
 */
export function parseAccountUrl(raw: string, hint?: PlatformKey | null): ParsedAccount | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // A bare handle only means something once a platform has been named. Dots are allowed in one
  // (Instagram's often have them): "@mel.tracker" or "mel.tracker" with Instagram picked was read as
  // a website, https://mel.tracker/ (2026-11-04). Links and Other are for websites, so they keep it.
  const bareHandle = !/[/:]/.test(trimmed) && (trimmed.startsWith("@") || (hint != null && hint !== "linktree" && hint !== "other"));
  if (!/[./]/.test(trimmed) || bareHandle) {
    if (!hint) return null;
    const handle = trimmed.replace(/^@/, "");
    return { platform: hint, url: platform(hint).urlFor(handle), handle };
  }

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  const found = PLATFORMS.find((entry) => entry.hosts.some((candidate) => host === candidate || host.endsWith(`.${candidate}`)));

  if (!found || found.key === "other") {
    // Tracking parameters are stripped so the same profile shared twice does not
    // become two rows.
    url.search = "";
    url.hash = "";
    return { platform: hint ?? "other", url: url.toString(), handle: firstSegment(url) };
  }

  const handle = found.handleFrom(url);
  return {
    platform: found.key,
    url: handle ? found.urlFor(handle) : (() => {
      url.search = "";
      url.hash = "";
      return url.toString();
    })(),
    handle,
  };
}
